import { LanguageCode, TranslationNamespace, InterpolationParams } from '@/types/i18n';
import { log } from '@/utils/wizardLoggers';
import { configureClientLogging } from '@/utils/wizardLogger';
import { configureEncounterRollout } from '@/config/encounterRollout';
import { localeCache } from './localeCache';
import { parseLocalePayload } from './localeParser';
import {
  getRouteLocaleDescriptor,
  type RouteLocaleDescriptor,
} from './routeLocaleManifest';
import {
  configureLocaleDiagnostics,
  LocaleRuntimeRequestError,
  makeLocaleCorrelationId,
  recordLocaleDiagnostic,
  type LocaleFailureReason,
} from './localeDiagnostics';

const DEFAULT_LANGUAGE: LanguageCode = 'en';
const SUPPORTED_LANGUAGES: LanguageCode[] = ['en', 'ru', 'fr', 'de', 'es'];
const bootstrapLocaleLoaders: Record<
  LanguageCode,
  () => Promise<{ default: TranslationNamespace }>
> = {
  en: () => import('./generated/locale-release/en.json'),
  ru: () => import('./generated/locale-release/ru.json'),
  fr: () => import('./generated/locale-release/fr.json'),
  de: () => import('./generated/locale-release/de.json'),
  es: () => import('./generated/locale-release/es.json'),
};

async function loadBootstrapTranslations(
  language: LanguageCode,
): Promise<TranslationNamespace> {
  const existing = window.__PF2E_TRANSLATIONS__?.[language];
  if (existing && typeof existing === 'object') {
    return existing as TranslationNamespace;
  }

  const localeModule = await bootstrapLocaleLoaders[language]();
  const translations = localeModule.default;
  const bootstrapTranslations = window.__PF2E_TRANSLATIONS__ ??= {};
  bootstrapTranslations[language] = translations;
  return translations;
}

// ── Missing key tracker ───────────────────────────────────────────────────────
const _missingKeys = new Set<string>();

function _trackMissingKey(key: string, language: LanguageCode): void {
  const entry = `${language}:${key}`;
  if (_missingKeys.has(entry)) return;
  _missingKeys.add(entry);
  log.i18n.warn('Missing translation key', { key, language });
}

// ── Lazy bootstrap bundles generated from the active DB release ─────────────
function _getEnBundle(): TranslationNamespace | null {
  return (window.__PF2E_TRANSLATIONS__?.['en'] as TranslationNamespace) ?? null;
}
const IS_DEV = import.meta.env.DEV;

type LocaleMemoryEntry = {
  translations: TranslationNamespace;
  routeNamespaces: Set<string>;
  hasAllRouteUi: boolean;
  hasVocabulary: boolean;
};

const translationsCache: Map<string, LocaleMemoryEntry> = new Map();
const MAX_MEMORY_CACHE_ENTRIES = 5;
const inFlightTranslations: Map<string, Promise<LocaleMemoryEntry>> = new Map();
const localeVersionCache: { value: LocaleMeta | null } = { value: null };

type LocaleMeta = {
  version: string;
  generated_at: string;
  mode?: 'release';
  contract_version?: string | null;
  release_id?: string | null;
  previous_release_id?: string | null;
  frontend_compatibility?: string[];
  locales?: Record<string, unknown>;
  delivery?: {
    segmented?: boolean;
    required_segments?: string[];
    optional_segments?: string[];
    segments?: Record<string, {
      required?: boolean;
      mount?: 'root' | 'pf2e';
      supports_namespaces?: boolean;
    }>;
  };
};

type LocaleRuntimeConfig = {
  client_log_enabled?: boolean;
  client_log_sampling_rate?: number;
  encounter_trait_popover_enabled?: boolean;
  encounter_pagination_enabled?: boolean;
};

type LoadTranslationsOptions = {
  force?: boolean;
  route?: RouteLocaleDescriptor;
};

type RuntimeRequestContext = {
  requestedLocale?: LanguageCode;
  releaseVersion?: string;
  routeId: string;
};

export type LocaleDegradedFallback = null | 'previous_locale' | 'english_bundle' | 'bootstrap_bundle';

export type LoadTranslationsResult = {
  requestedLanguage: LanguageCode;
  actualLanguage: LanguageCode;
  translations: TranslationNamespace;
  version: string;
  degradedFallback: LocaleDegradedFallback;
  source: 'memory_cache' | 'session_cache' | 'network' | 'english_bundle';
  routeId: string | 'all';
};

type CachedLocalePayload = {
  version: string;
  translations: TranslationNamespace;
};

type I18nDiagnosticReason = 'target' | 'en_fallback' | 'default_value' | 'missing_key' | 'locale_not_ready';

type I18nDiagnosticEvent = {
  reason: I18nDiagnosticReason;
  key: string;
  requestedLanguage: LanguageCode;
  activeLanguage: LanguageCode | null;
  timestamp: number;
};

export type I18nDiagnostics = {
  targetHits: number;
  enFallbacks: number;
  defaultValueFallbacks: number;
  missingKeyFallbacks: number;
  localeNotReadyFallbacks: number;
  recentEvents: I18nDiagnosticEvent[];
};

function buildCacheKey(language: LanguageCode, version: string): string {
  return `${language}:${version}`;
}

function rememberTranslations(cacheKey: string, entry: LocaleMemoryEntry): void {
  translationsCache.delete(cacheKey);
  translationsCache.set(cacheKey, entry);
  while (translationsCache.size > MAX_MEMORY_CACHE_ENTRIES) {
    const oldestKey = translationsCache.keys().next().value as string | undefined;
    if (!oldestKey) break;
    translationsCache.delete(oldestKey);
  }
}

function discardLegacySessionCache(): void {
  if (typeof window === 'undefined') return;
  try {
    const keysToRemove: string[] = [];
    for (let index = 0; index < window.sessionStorage.length; index += 1) {
      const key = window.sessionStorage.key(index);
      if (key && (
        key.startsWith('pf2e_locale_latest_')
        || /^pf2e_locale_(en|ru|fr|de|es)_/.test(key)
      )) {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) window.sessionStorage.removeItem(key);
  } catch {
    // Browser storage cleanup is best-effort and never blocks activation.
  }
}

discardLegacySessionCache();

const diagnostics: I18nDiagnostics = {
  targetHits: 0,
  enFallbacks: 0,
  defaultValueFallbacks: 0,
  missingKeyFallbacks: 0,
  localeNotReadyFallbacks: 0,
  recentEvents: [],
};

function recordDiagnostic(
  reason: I18nDiagnosticReason,
  key: string,
  requestedLanguage: LanguageCode,
  activeLanguage: LanguageCode | null,
): void {
  if (!IS_DEV) return;

  if (reason === 'target') diagnostics.targetHits += 1;
  if (reason === 'en_fallback') diagnostics.enFallbacks += 1;
  if (reason === 'default_value') diagnostics.defaultValueFallbacks += 1;
  if (reason === 'missing_key') diagnostics.missingKeyFallbacks += 1;
  if (reason === 'locale_not_ready') diagnostics.localeNotReadyFallbacks += 1;

  diagnostics.recentEvents.unshift({
    reason,
    key,
    requestedLanguage,
    activeLanguage,
    timestamp: Date.now(),
  });
  diagnostics.recentEvents.splice(50);
}

function getBootstrapTranslations(language: LanguageCode): TranslationNamespace | null {
  const bootstrapData = window.__PF2E_TRANSLATIONS__?.[language];
  if (!bootstrapData || typeof bootstrapData !== 'object') {
    return null;
  }

  return bootstrapData as TranslationNamespace;
}

function failureReasonForStatus(status: number): LocaleFailureReason {
  if (status === 409) return 'release_unavailable';
  if (status === 503) return 'runtime_unavailable';
  if (status >= 500) return 'http_5xx';
  if (status >= 400) return 'http_4xx';
  return 'unknown';
}

async function fetchParsedRuntime<T>(
  url: string,
  init: RequestInit,
  context: RuntimeRequestContext,
  parse: (text: string) => Promise<T> | T,
): Promise<T> {
  const outboundCorrelationId = makeLocaleCorrelationId();
  const fetchStarted = performance.now();
  let response: Response;
  try {
    const headers = new Headers(init.headers);
    headers.set('X-Correlation-ID', outboundCorrelationId);
    response = await fetch(url, {
      ...init,
      headers,
    });
  } catch {
    recordLocaleDiagnostic({
      event_type: 'runtime_request_failure',
      requested_locale: context.requestedLocale,
      release_version: context.releaseVersion,
      correlation_id: outboundCorrelationId,
      route_id: context.routeId,
      fetch_ms: performance.now() - fetchStarted,
      failure_reason: 'network',
    });
    throw new LocaleRuntimeRequestError('Locale runtime request failed', 'network', outboundCorrelationId);
  }
  const correlationId = response.headers.get('X-Correlation-ID') ?? outboundCorrelationId;
  const fetchMs = performance.now() - fetchStarted;
  if (!response.ok) {
    const reason = failureReasonForStatus(response.status);
    recordLocaleDiagnostic({
      event_type: 'runtime_request_failure',
      requested_locale: context.requestedLocale,
      release_version: context.releaseVersion,
      correlation_id: correlationId,
      route_id: context.routeId,
      fetch_ms: fetchMs,
      failure_reason: reason,
    });
    throw new LocaleRuntimeRequestError(
      `Locale runtime request failed (${response.status})`,
      reason,
      correlationId,
    );
  }

  const text = await response.text();
  const parseStarted = performance.now();
  try {
    const parsed = await parse(text);
    recordLocaleDiagnostic({
      event_type: 'runtime_request_success',
      requested_locale: context.requestedLocale,
      release_version: context.releaseVersion,
      correlation_id: correlationId,
      route_id: context.routeId,
      fetch_ms: fetchMs,
      parse_ms: performance.now() - parseStarted,
      total_ms: performance.now() - fetchStarted,
    });
    return parsed;
  } catch {
    recordLocaleDiagnostic({
      event_type: 'parse_failure',
      requested_locale: context.requestedLocale,
      release_version: context.releaseVersion,
      correlation_id: correlationId,
      route_id: context.routeId,
      fetch_ms: fetchMs,
      parse_ms: performance.now() - parseStarted,
      failure_reason: 'parse',
    });
    throw new LocaleRuntimeRequestError('Locale payload parsing failed', 'parse', correlationId);
  }
}

export async function fetchRuntimeLocaleMeta(force = false): Promise<LocaleMeta> {
  if (!force && localeVersionCache.value) {
    return localeVersionCache.value;
  }

  const [meta, runtimeConfig] = await Promise.all([
    fetchParsedRuntime(
      '/api/localization/runtime/meta',
      { cache: 'no-store' },
      { routeId: 'meta' },
      text => JSON.parse(text) as LocaleMeta,
    ),
    fetchRuntimeLocaleConfig(),
  ]);
  localeVersionCache.value = meta;
  if (runtimeConfig.client_log_enabled !== undefined) {
    configureClientLogging(
      runtimeConfig.client_log_enabled,
      runtimeConfig.client_log_sampling_rate ?? 1.0,
    );
    configureLocaleDiagnostics(
      runtimeConfig.client_log_enabled,
      runtimeConfig.client_log_sampling_rate ?? 1.0,
    );
  }
  configureEncounterRollout({
    traitPopoverEnabled: runtimeConfig.encounter_trait_popover_enabled,
    paginationEnabled: runtimeConfig.encounter_pagination_enabled,
  });
  return meta;
}

async function fetchRuntimeLocaleConfig(): Promise<LocaleRuntimeConfig> {
  try {
    return await fetchParsedRuntime(
      '/api/localization/runtime/config',
      { cache: 'no-store' },
      { routeId: 'config' },
      text => JSON.parse(text) as LocaleRuntimeConfig,
    );
  } catch (error) {
    // Locale activation must not be coupled to optional operational flags.
    console.warn('Locale runtime config is unavailable:', error);
    return {};
  }
}

async function fetchRuntimeTranslations(
  language: LanguageCode,
  version: string
): Promise<TranslationNamespace> {
  return await fetchParsedRuntime(
    `/api/localization/runtime/${language}?v=${encodeURIComponent(version)}`,
    {
      cache: IS_DEV ? 'no-store' : 'default',
      headers: IS_DEV ? { 'Cache-Control': 'no-cache' } : undefined,
    },
    { requestedLocale: language, releaseVersion: version, routeId: 'full' },
    parseLocalePayload,
  );
}

async function fetchRuntimeSegment(
  language: LanguageCode,
  version: string,
  segment: 'ui_core' | 'route_ui' | 'pf2e_vocabulary',
  namespaces: readonly string[] = [],
): Promise<TranslationNamespace> {
  const params = new URLSearchParams({ v: version });
  if (namespaces.length > 0) {
    params.set('namespaces', [...new Set(namespaces)].sort().join(','));
  }
  return await fetchParsedRuntime(
    `/api/localization/runtime/${language}/segments/${segment}?${params.toString()}`,
    {
      cache: IS_DEV ? 'no-store' : 'default',
      headers: IS_DEV ? { 'Cache-Control': 'no-cache' } : undefined,
    },
    {
      requestedLocale: language,
      releaseVersion: version,
      routeId: `segment:${segment}`,
    },
    parseLocalePayload,
  );
}

function mergeTranslations(
  ...parts: TranslationNamespace[]
): TranslationNamespace {
  const merged: TranslationNamespace = {};
  for (const part of parts) {
    for (const [key, value] of Object.entries(part)) {
      if (key in merged && merged[key] !== value) {
        throw new Error(`Duplicate locale segment root: ${key}`);
      }
      merged[key] = value;
    }
  }
  return merged;
}

function supportsSegmentedDelivery(meta: LocaleMeta): boolean {
  return meta.delivery?.segmented === true
    && meta.delivery.required_segments?.includes('ui_core') === true
    && meta.delivery.required_segments?.includes('route_ui') === true;
}

function coversRoute(entry: LocaleMemoryEntry, route: RouteLocaleDescriptor): boolean {
  return entry.hasAllRouteUi
    || route.namespaces.every((namespace) => entry.routeNamespaces.has(namespace));
}

function readSessionTranslations(
  language: LanguageCode,
  version: string,
): TranslationNamespace | null {
  if (IS_DEV) {
    return null;
  }

  const cached = localeCache.read(language, version);
  if (cached.status === 'hit') {
    recordLocaleDiagnostic({
      event_type: 'cache_hit',
      requested_locale: language,
      release_version: version,
      cache_source: 'session_cache',
      storage_status: 'hit',
    });
    return cached.translations;
  }
  recordLocaleDiagnostic({
    event_type: cached.status === 'unavailable'
      ? 'storage_unavailable'
      : (cached.status === 'corrupt' ? 'corrupt_cache' : 'cache_miss'),
    requested_locale: language,
    release_version: version,
    storage_status: cached.status === 'miss' ? 'miss' : cached.status,
    failure_reason: cached.status === 'unavailable'
      ? 'storage_unavailable'
      : (cached.status === 'corrupt' ? 'cache_corrupt' : undefined),
  });

  return null;
}

export function getCachedTranslationsForLanguage(language: LanguageCode): CachedLocalePayload | null {
  if (IS_DEV) {
    return null;
  }

  const cached = localeCache.read(language);
  if (cached.status === 'hit') {
    recordLocaleDiagnostic({
      event_type: 'cache_hit',
      requested_locale: language,
      release_version: cached.version,
      cache_source: 'session_cache',
      storage_status: 'hit',
    });
    return { version: cached.version, translations: cached.translations };
  }
  recordLocaleDiagnostic({
    event_type: cached.status === 'unavailable'
      ? 'storage_unavailable'
      : (cached.status === 'corrupt' ? 'corrupt_cache' : 'cache_miss'),
    requested_locale: language,
    storage_status: cached.status === 'miss' ? 'miss' : cached.status,
    failure_reason: cached.status === 'unavailable'
      ? 'storage_unavailable'
      : (cached.status === 'corrupt' ? 'cache_corrupt' : undefined),
  });

  return null;
}

function writeSessionTranslations(
  language: LanguageCode,
  version: string,
  translations: TranslationNamespace,
): void {
  if (IS_DEV) {
    return;
  }

  const result = localeCache.write(language, version, translations);
  if (result.status !== 'stored') {
    recordLocaleDiagnostic({
      event_type: result.status === 'quota'
        ? 'storage_quota'
        : (result.status === 'corrupt' ? 'corrupt_cache' : 'storage_unavailable'),
      requested_locale: language,
      release_version: version,
      storage_status: result.status,
      failure_reason: result.status === 'quota'
        ? 'storage_quota'
        : (result.status === 'corrupt' ? 'cache_corrupt' : 'storage_unavailable'),
    });
  }
}

export function markLocaleCacheActive(
  language: LanguageCode | null,
  version: string | null,
): void {
  localeCache.protect(language, version);
}

export function getBootstrapTranslationsForLanguage(language: LanguageCode): TranslationNamespace | null {
  return getBootstrapTranslations(language);
}

export async function ensureBootstrapTranslationsForLanguage(
  language: LanguageCode,
): Promise<TranslationNamespace> {
  const existing = getBootstrapTranslations(language);
  if (existing) {
    return existing;
  }

  return await loadBootstrapTranslations(language) as TranslationNamespace;
}

async function _fetchWithBackoff(
  language: LanguageCode,
  version: string,
  meta: LocaleMeta,
  route: RouteLocaleDescriptor,
  attempt = 0,
): Promise<LocaleMemoryEntry> {
  try {
    if (!supportsSegmentedDelivery(meta)) {
      return {
        translations: await fetchRuntimeTranslations(language, version),
        routeNamespaces: new Set(),
        hasAllRouteUi: true,
        hasVocabulary: true,
      };
    }

    const [core, routeUi] = await Promise.all([
      fetchRuntimeSegment(language, version, 'ui_core'),
      fetchRuntimeSegment(language, version, 'route_ui', route.namespaces),
    ]);
    return {
      translations: mergeTranslations(core, routeUi),
      routeNamespaces: new Set(route.namespaces),
      hasAllRouteUi: false,
      hasVocabulary: false,
    };
  } catch (err) {
    if (attempt < 3) {
      const delay = Math.min(1000 * 2 ** attempt, 8000);
      await new Promise(r => setTimeout(r, delay));
      return _fetchWithBackoff(language, version, meta, route, attempt + 1);
    }
    throw err;
  }
}

function fetchTranslationsOnce(
  language: LanguageCode,
  version: string,
  meta: LocaleMeta,
  route: RouteLocaleDescriptor,
): Promise<LocaleMemoryEntry> {
  const cacheKey = `${buildCacheKey(language, version)}:${route.id}`;
  const existing = inFlightTranslations.get(cacheKey);
  if (existing) {
    return existing;
  }

  const request = _fetchWithBackoff(language, version, meta, route).finally(() => {
    if (inFlightTranslations.get(cacheKey) === request) {
      inFlightTranslations.delete(cacheKey);
    }
  });
  inFlightTranslations.set(cacheKey, request);
  return request;
}

export async function loadTranslations(
  language: LanguageCode,
  options: LoadTranslationsOptions = {}
): Promise<LoadTranslationsResult> {
  if (!SUPPORTED_LANGUAGES.includes(language)) {
    throw new Error(`Unsupported language: ${language}`);
  }

  const meta = await fetchRuntimeLocaleMeta(options.force);
  const cacheKey = buildCacheKey(language, meta.version);
  const route = options.route ?? getRouteLocaleDescriptor(window.location.pathname);

  if (!options.force) {
    const cached = translationsCache.get(cacheKey);
    if (cached && coversRoute(cached, route)) {
      rememberTranslations(cacheKey, cached);
      recordLocaleDiagnostic({
        event_type: 'cache_hit',
        requested_locale: language,
        release_version: meta.version,
        cache_source: 'memory_cache',
        storage_status: 'hit',
        route_id: route.id,
      });
      return {
        requestedLanguage: language,
        actualLanguage: language,
        translations: cached.translations,
        version: meta.version,
        degradedFallback: null,
        source: 'memory_cache',
        routeId: cached.hasAllRouteUi ? 'all' : route.id,
      };
    }
    if (cached && supportsSegmentedDelivery(meta)) {
      const missingNamespaces = route.namespaces.filter(
        (namespace) => !cached.routeNamespaces.has(namespace),
      );
      const routeUi = await fetchRuntimeSegment(
        language,
        meta.version,
        'route_ui',
        missingNamespaces,
      );
      const current = translationsCache.get(cacheKey) ?? cached;
      const stillMissing = missingNamespaces.filter(
        (namespace) => !current.routeNamespaces.has(namespace),
      );
      const relevantRouteUi = Object.fromEntries(
        Object.entries(routeUi).filter(([namespace]) => stillMissing.includes(namespace)),
      ) as TranslationNamespace;
      const updated: LocaleMemoryEntry = {
        ...current,
        translations: mergeTranslations(current.translations, relevantRouteUi),
        routeNamespaces: new Set([
          ...current.routeNamespaces,
          ...stillMissing,
        ]),
      };
      rememberTranslations(cacheKey, updated);
      return {
        requestedLanguage: language,
        actualLanguage: language,
        translations: updated.translations,
        version: meta.version,
        degradedFallback: null,
        source: 'network',
        routeId: route.id,
      };
    }
  }

  const cachedSessionTranslations = !options.force
    ? readSessionTranslations(language, meta.version)
    : null;
  if (cachedSessionTranslations) {
    const entry: LocaleMemoryEntry = {
      translations: cachedSessionTranslations,
      routeNamespaces: new Set(),
      hasAllRouteUi: true,
      hasVocabulary: true,
    };
    rememberTranslations(cacheKey, entry);
    return {
      requestedLanguage: language,
      actualLanguage: language,
      translations: cachedSessionTranslations,
      version: meta.version,
      degradedFallback: null,
      source: 'session_cache',
      routeId: 'all',
    };
  }

  try {
    const entry = await fetchTranslationsOnce(language, meta.version, meta, route);
    rememberTranslations(cacheKey, entry);
    if (entry.hasAllRouteUi && entry.hasVocabulary) {
      writeSessionTranslations(language, meta.version, entry.translations);
    }
    return {
      requestedLanguage: language,
      actualLanguage: language,
      translations: entry.translations,
      version: meta.version,
      degradedFallback: null,
      source: 'network',
      routeId: entry.hasAllRouteUi ? 'all' : route.id,
    };
  } catch (error) {
    console.error(`Error loading translations for ${language} (after retries):`, error);
    if (language !== DEFAULT_LANGUAGE) {
      throw error;
    }

    const enBundle = _getEnBundle() ?? await ensureBootstrapTranslationsForLanguage(DEFAULT_LANGUAGE);
    if (enBundle) {
      return {
        requestedLanguage: language,
        actualLanguage: DEFAULT_LANGUAGE,
        translations: enBundle,
        version: 'bundle-fallback',
        degradedFallback: 'english_bundle',
        source: 'english_bundle',
        routeId: 'all',
      };
    }
    throw error;
  }
}

export async function loadOptionalVocabulary(
  language: LanguageCode,
  version: string,
): Promise<TranslationNamespace | null> {
  const meta = await fetchRuntimeLocaleMeta();
  if (!supportsSegmentedDelivery(meta) || meta.version !== version) return null;
  const cacheKey = buildCacheKey(language, version);
  const cached = translationsCache.get(cacheKey);
  if (!cached || cached.hasVocabulary) return cached?.translations ?? null;

  const vocabulary = await fetchRuntimeSegment(
    language,
    version,
    'pf2e_vocabulary',
  );
  const current = translationsCache.get(cacheKey);
  if (!current) return null;
  if (current.hasVocabulary) return current.translations;
  const updated: LocaleMemoryEntry = {
    ...current,
    translations: mergeTranslations(
      current.translations,
      { pf2e: vocabulary } as unknown as TranslationNamespace,
    ),
    hasVocabulary: true,
  };
  rememberTranslations(cacheKey, updated);
  return updated.translations;
}

export async function prefetchRouteTranslations(
  language: LanguageCode,
  version: string,
  route: RouteLocaleDescriptor,
): Promise<void> {
  const meta = await fetchRuntimeLocaleMeta();
  if (!supportsSegmentedDelivery(meta) || meta.version !== version) return;
  const cacheKey = buildCacheKey(language, version);
  const cached = translationsCache.get(cacheKey);
  if (!cached || coversRoute(cached, route)) return;
  const missingNamespaces = route.namespaces.filter(
    (namespace) => !cached.routeNamespaces.has(namespace),
  );
  const routeUi = await fetchRuntimeSegment(
    language,
    version,
    'route_ui',
    missingNamespaces,
  );
  const current = translationsCache.get(cacheKey);
  if (!current) return;
  const stillMissing = missingNamespaces.filter(
    (namespace) => !current.routeNamespaces.has(namespace),
  );
  if (stillMissing.length === 0) return;
  const relevantRouteUi = Object.fromEntries(
    Object.entries(routeUi).filter(([namespace]) => stillMissing.includes(namespace)),
  ) as TranslationNamespace;
  rememberTranslations(cacheKey, {
    ...current,
    translations: mergeTranslations(current.translations, relevantRouteUi),
    routeNamespaces: new Set([
      ...current.routeNamespaces,
      ...stillMissing,
    ]),
  });
}

/**
 * Получение значения из объекта по пути.
 */
function getNestedValue(obj: Record<string, any>, path: string): any {
  if (obj[path] !== undefined) {
    return obj[path];
  }

  return path.split('.').reduce((acc, part) => acc?.[part], obj);
}

/**
 * Интерполяция параметров в строку перевода
 */
function interpolate(text: string, params?: InterpolationParams): string {
  if (!params) return text;

  return Object.entries(params).reduce((result, [key, value]) => {
    const escapedValue = String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&var(--m13-color-039);');

    return result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), escapedValue);
  }, text);
}

/**
 * Создание функции перевода с fallback chain:
 * 1. Target language
 * 2. EN bundle (inlined) or EN translationsCache
 * 3. defaultValue
 * 4. Key (with [MISSING:] marker in DEV)
 */
export function createTranslateFunction(
  translations: TranslationNamespace,
  language: LanguageCode,
  options: {
    requestedLanguage?: LanguageCode;
    localeReady?: boolean;
  } = {},
): (key: string, params?: InterpolationParams, defaultValue?: string) => string {
  const requestedLanguage = options.requestedLanguage ?? language;
  const localeReady = options.localeReady ?? requestedLanguage === language;

  return function t(
    key: string,
    params?: InterpolationParams,
    defaultValue?: string
  ): string {
    // 1. Target language
    const value = getNestedValue(translations, key);
    if (value !== undefined && typeof value === 'string') {
      if (!localeReady) {
        recordDiagnostic('locale_not_ready', key, requestedLanguage, language);
      } else {
        recordDiagnostic('target', key, requestedLanguage, language);
      }
      return interpolate(value, params);
    }

    // 2. EN fallback (bundle or cache)
    if (language !== DEFAULT_LANGUAGE) {
      // 2a. Lazy EN bootstrap bundle (available after it has been requested)
      const enBundle = _getEnBundle();
      if (enBundle) {
        const enVal = getNestedValue(enBundle, key);
        if (enVal !== undefined && typeof enVal === 'string') {
          _trackMissingKey(key, language);
          recordDiagnostic('en_fallback', key, requestedLanguage, language);
          return interpolate(enVal, params);
        }
      }
      // 2b. EN from translationsCache (if EN was fetched this session)
      for (const [cacheKey, cachedEntry] of translationsCache.entries()) {
        if (!cacheKey.startsWith(`${DEFAULT_LANGUAGE}:`)) continue;
        const enVal = getNestedValue(cachedEntry.translations, key);
        if (enVal !== undefined && typeof enVal === 'string') {
          _trackMissingKey(key, language);
          recordDiagnostic('en_fallback', key, requestedLanguage, language);
          return interpolate(enVal, params);
        }
      }
      _trackMissingKey(key, language);
    }

    // 3. defaultValue (explicit '' допустим — пропускаем только undefined)
    if (defaultValue !== undefined) {
      recordDiagnostic('default_value', key, requestedLanguage, language);
      return defaultValue;
    }

    // 4. Last resort
    recordDiagnostic('missing_key', key, requestedLanguage, language);
    if (import.meta.env.DEV) return `[MISSING: ${key}]`;
    return key;
  };
}

export function recordLocaleNotReadyFallback(
  key: string,
  requestedLanguage: LanguageCode,
  defaultValue?: string,
): void {
  recordDiagnostic('locale_not_ready', key, requestedLanguage, null);
  if (defaultValue !== undefined) {
    recordDiagnostic('default_value', key, requestedLanguage, null);
  }
}

/**
 * Очистка кэша переводов.
 */
export function clearTranslationsCache(): void {
  translationsCache.clear();
  inFlightTranslations.clear();
  localeVersionCache.value = null;
  localeCache.clear();

  discardLegacySessionCache();
}

export function getCachedLocaleVersion(): string | null {
  return localeVersionCache.value?.version ?? null;
}

export async function getLatestLocaleVersion(): Promise<string> {
  const meta = await fetchRuntimeLocaleMeta(true);
  return meta.version;
}

export function getI18nDiagnostics(): I18nDiagnostics {
  return {
    ...diagnostics,
    recentEvents: diagnostics.recentEvents.slice(),
  };
}

export function resetI18nDiagnostics(): void {
  diagnostics.targetHits = 0;
  diagnostics.enFallbacks = 0;
  diagnostics.defaultValueFallbacks = 0;
  diagnostics.missingKeyFallbacks = 0;
  diagnostics.localeNotReadyFallbacks = 0;
  diagnostics.recentEvents = [];
}
